import {
  COUNTERPARTY_ROLE_LABELS,
  COUNTERPARTY_ROLES,
  counterpartyRoleSchema,
  type CounterpartySummary,
} from "@station/contracts";
import {
  Alert,
  Button,
  DataTable,
  type DataTableColumn,
  EmptyState,
  FormField,
  Input,
  MoneyText,
  Select,
  StatusBadge,
} from "@station/ui";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ArrowLeftRight, Plus, Upload, Users } from "lucide-react";
import { type ReactElement, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { companySearchShape } from "../../../features/accounting/company.js";
import {
  formatMinorText,
  formatSignedBalance,
  isOverLimit,
  maskTaxId,
} from "../../../features/counterparties/balance.js";
import { CounterpartyPage, RoleBadges, SignedBalance } from "../../../features/counterparties/shared.js";
import { api } from "../../../lib/api.js";
import { useCursorPages } from "../../../lib/cursor-pages.js";
import { errorMessage } from "../../../lib/errors.js";
import { exportRowsToExcel, fetchAllPages } from "../../../lib/excel.js";
import { queryKeys } from "../../../lib/query.js";
import { usePermissions } from "../../../lib/session.js";

const STATES = ["active", "inactive", "blocked", "balance"] as const;
const searchSchema = z.object({
  ...companySearchShape,
  q: z.string().max(100).optional().catch(undefined),
  role: counterpartyRoleSchema.optional().catch(undefined),
  state: z.enum(STATES).optional().catch(undefined),
});

export const Route = createFileRoute("/_app/cari/")({
  validateSearch: (search) => searchSchema.parse(search),
  component: CounterpartiesPage,
});

function CounterpartiesPage() {
  const { t } = useTranslation();
  const permissions = usePermissions();
  return (
    <CounterpartyPage
      title={t("menu.counterparties")}
      description={t("counterparties.list.description")}
      actions={
        <>
          {permissions.has("counterparty.txn.create") ? (
            <>
              <Button variant="outline" asChild>
                <Link to="/cari/acilis-aktarimi">
                  <Upload aria-hidden="true" />
                  {t("counterparties.list.openingImport")}
                </Link>
              </Button>
              <Button variant="outline" asChild>
                <Link to="/cari/hareket-yeni">
                  <ArrowLeftRight aria-hidden="true" />
                  {t("menu.counterpartyTransaction")}
                </Link>
              </Button>
            </>
          ) : null}
          {permissions.has("counterparty.card.manage") ? (
            <Button asChild>
              <Link to="/cari/yeni">
                <Plus aria-hidden="true" />
                {t("counterparties.list.new")}
              </Link>
            </Button>
          ) : null}
        </>
      }
    >
      {({ companyId }) => <CounterpartyList companyId={companyId} />}
    </CounterpartyPage>
  );
}

function CounterpartyList({ companyId }: { companyId: string }): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate({ from: Route.fullPath });
  const { company: _company, ...filters } = Route.useSearch();
  const [searchText, setSearchText] = useState(filters.q ?? "");
  const pages = useCursorPages(JSON.stringify([companyId, filters]));
  const setFilters = (patch: Partial<typeof filters>) =>
    void navigate({
      search: (previous) =>
        Object.fromEntries(Object.entries({ ...previous, ...patch }).filter(([, value]) => value)),
      replace: true,
    });

  useEffect(() => {
    const handle = setTimeout(() => {
      if ((filters.q ?? "") !== searchText.trim()) setFilters({ q: searchText.trim() || undefined });
    }, 300);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- yalnızca yazılan metin değişince çalışmalı
  }, [searchText]);

  const query = {
    companyId,
    limit: 25,
    ...(filters.q ? { search: filters.q } : {}),
    ...(filters.role ? { role: filters.role } : {}),
    ...(filters.state === "active" ? { isActive: "true" as const } : {}),
    ...(filters.state === "inactive" ? { isActive: "false" as const } : {}),
    ...(filters.state === "blocked" ? { isBlocked: "true" as const } : {}),
    ...(filters.state === "balance" ? { onlyWithBalance: "true" as const } : {}),
  };
  const list = useQuery({
    queryKey: queryKeys.counterparties(companyId, "list", { ...query, cursor: pages.cursor ?? null }),
    queryFn: async () =>
      (
        await api.GET("/counterparties", {
          params: { query: { ...query, ...(pages.cursor ? { cursor: pages.cursor } : {}) } },
        })
      ).data!,
    placeholderData: keepPreviousData,
  });

  const columns = useMemo<DataTableColumn<CounterpartySummary>[]>(
    () => [
      {
        id: "code",
        header: t("counterparties.fields.code"),
        hideable: false,
        cell: (row) => (
          <Link
            to="/cari/$id"
            params={{ id: row.id }}
            className="font-mono font-medium text-primary underline-offset-4 hover:underline"
            onClick={(event) => event.stopPropagation()}
          >
            {row.code}
          </Link>
        ),
        exportValue: (row) => row.code,
      },
      {
        id: "name",
        header: t("counterparties.fields.name"),
        hideable: false,
        cell: (row) => (
          <span className="flex min-w-48 flex-wrap items-center gap-2">
            <span className="font-medium">{row.name}</span>
            {row.isBlocked ? (
              <StatusBadge tone="danger">{t("counterparties.badges.blocked")}</StatusBadge>
            ) : null}
            {row.isActive ? null : <StatusBadge tone="neutral">{t("common.inactive")}</StatusBadge>}
          </span>
        ),
        exportValue: (row) => row.name,
      },
      {
        id: "roles",
        header: t("counterparties.fields.roles"),
        cell: (row) => <RoleBadges roles={row.roles} />,
        exportValue: (row) => row.roles.map((role) => COUNTERPARTY_ROLE_LABELS[role]).join(", "),
      },
      {
        id: "taxId",
        header: t("counterparties.fields.taxId"),
        defaultVisible: false,
        // KVKK: TCKN listede maskelenir; VKN açık kalır.
        cell: (row) => <span className="tabular">{maskTaxId(row.taxId)}</span>,
        exportValue: (row) => maskTaxId(row.taxId),
      },
      {
        id: "balance",
        header: t("counterparties.fields.balance"),
        align: "right",
        cell: (row) => <SignedBalance value={row.balance} className="font-medium" />,
        exportValue: (row) => formatSignedBalance(row.balance, { symbol: false }),
      },
      {
        id: "overdueReceivable",
        header: t("counterparties.fields.overdueReceivable"),
        align: "right",
        cell: (row) =>
          row.overdueReceivable === "0" ? (
            <span className="text-muted-foreground">—</span>
          ) : (
            <MoneyText value={row.overdueReceivable} className="font-medium text-destructive" />
          ),
        exportValue: (row) => formatMinorText(row.overdueReceivable),
      },
      {
        id: "overduePayable",
        header: t("counterparties.fields.overduePayable"),
        align: "right",
        defaultVisible: false,
        cell: (row) =>
          row.overduePayable === "0" ? (
            <span className="text-muted-foreground">—</span>
          ) : (
            <MoneyText value={row.overduePayable} />
          ),
        exportValue: (row) => formatMinorText(row.overduePayable),
      },
      {
        id: "risk",
        header: t("counterparties.fields.risk"),
        align: "right",
        cell: (row) => (
          <span className="inline-flex items-center justify-end gap-2">
            {isOverLimit(row.risk, row.creditLimit) ? (
              <StatusBadge tone="danger" className="whitespace-nowrap">
                {t("counterparties.badges.overLimit")}
              </StatusBadge>
            ) : null}
            <MoneyText value={row.risk} />
          </span>
        ),
        exportValue: (row) => formatMinorText(row.risk),
      },
      {
        id: "creditLimit",
        header: t("counterparties.fields.creditLimit"),
        align: "right",
        cell: (row) =>
          row.creditLimit === null ? (
            <span className="text-muted-foreground">{t("counterparties.list.unlimited")}</span>
          ) : (
            <MoneyText value={row.creditLimit} />
          ),
        exportValue: (row) =>
          row.creditLimit === null ? t("counterparties.list.unlimited") : formatMinorText(row.creditLimit),
      },
    ],
    [t],
  );
  const hasFilters = Object.values(filters).some(Boolean);

  return (
    <DataTable
      caption={t("menu.counterparties")}
      columns={columns}
      rows={list.data?.items ?? []}
      rowKey={(row) => row.id}
      loading={list.isPending}
      error={list.error ? <Alert tone="danger">{errorMessage(list.error)}</Alert> : undefined}
      emptyState={
        <EmptyState
          headingLevel={2}
          icon={<Users />}
          title={hasFilters ? t("counterparties.list.emptyFiltered") : t("counterparties.list.empty")}
          description={
            hasFilters ? t("counterparties.list.emptyFilteredHint") : t("counterparties.list.emptyHint")
          }
        />
      }
      filters={
        <>
          <FormField label={t("common.search")} className="w-60">
            {(control) => (
              <Input
                {...control}
                type="search"
                value={searchText}
                onChange={(event) => setSearchText(event.target.value)}
                placeholder={t("counterparties.list.searchPlaceholder")}
              />
            )}
          </FormField>
          <FormField label={t("counterparties.fields.role")} className="w-44">
            {(control) => (
              <Select
                {...control}
                value={filters.role ?? ""}
                onChange={(event) =>
                  setFilters({ role: (event.target.value || undefined) as typeof filters.role })
                }
              >
                <option value="">{t("common.all")}</option>
                {COUNTERPARTY_ROLES.map((role) => (
                  <option key={role} value={role}>
                    {COUNTERPARTY_ROLE_LABELS[role]}
                  </option>
                ))}
              </Select>
            )}
          </FormField>
          <FormField label={t("common.status")} className="w-44">
            {(control) => (
              <Select
                {...control}
                value={filters.state ?? ""}
                onChange={(event) =>
                  setFilters({ state: (event.target.value || undefined) as typeof filters.state })
                }
              >
                <option value="">{t("common.all")}</option>
                <option value="active">{t("common.active")}</option>
                <option value="inactive">{t("common.inactive")}</option>
                <option value="blocked">{t("counterparties.list.stateBlocked")}</option>
                <option value="balance">{t("counterparties.list.stateWithBalance")}</option>
              </Select>
            )}
          </FormField>
          {hasFilters ? (
            <Button
              variant="ghost"
              size="sm"
              className="mb-1"
              onClick={() => {
                setSearchText("");
                void navigate({ search: (previous) => ({ company: previous.company }), replace: true });
              }}
            >
              {t("common.clearFilters")}
            </Button>
          ) : null}
        </>
      }
      pagination={{
        hasPrevious: pages.hasPrevious,
        hasNext: list.data?.page.hasMore ?? false,
        onPrevious: pages.previous,
        onNext: () => pages.next(list.data?.page.nextCursor),
        pageLabel: t("common.pageNumber", { page: pages.pageNumber }),
      }}
      onExport={async (visible) => {
        const rows = await fetchAllPages(
          async (cursor) =>
            (
              await api.GET("/counterparties", {
                params: { query: { ...query, limit: 200, ...(cursor ? { cursor } : {}) } },
              })
            ).data!,
        );
        await exportRowsToExcel({
          fileName: "cariler",
          sheetName: t("menu.counterparties"),
          columns: visible,
          rows,
        });
      }}
      onRowClick={(row) => void navigate({ to: "/cari/$id", params: { id: row.id } })}
    />
  );
}
